// src/components/chart/chartUtils.ts

import { ChartData, ChartType, ChartSeriesData } from './types';
import { Options as HighchartsOptions } from 'highcharts';

export const SUPPORTED_CHART_TRANSITIONS: Record<ChartType, ChartType[]> = {
  area: ['line', 'column', 'areaspline'],
  line: ['area', 'spline', 'scatter'],
  column: ['bar', 'area', 'line'],
  bar: ['column', 'line'],
  pie: ['column', 'bar'],
  spline: ['line', 'areaspline'],
  areaspline: ['area', 'spline'],
  scatter: ['line', 'spline'],
  pyramid: ['bar', 'column'],
  horizontalStackedBar: [],
};

const HIGHCHARTS_TYPE_MAP: Partial<Record<ChartType, string>> = {
  horizontalStackedBar: 'bar',
};

export const getHighchartsType = (chartType: ChartType): string =>
  HIGHCHARTS_TYPE_MAP[chartType] ?? chartType;

const HORIZONTAL_STACKED_BAR_PRESET: HighchartsOptions = {
  chart: {
    spacingLeft: 0,
    spacingRight: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  legend: {
    enabled: true,
    reversed: false,
  },
  xAxis: {
    categories: [''],
    visible: false,
  },
  yAxis: {
    reversedStacks: false,
    min: 0,
    endOnTick: false,
    startOnTick: true,
    maxPadding: 0,
    minPadding: 0,
  },
  plotOptions: {
    series: {
      stacking: 'normal',
      animation: false,
    },
    bar: {
      stacking: 'normal',
      animation: false,
      dataLabels: {
        enabled: true,
        format: '{series.name} {point.y} ({point.percentage:.1f}%)',
        style: {
          color: '#FFFFFF',
          textOutline: 'none',
          fontWeight: '500',
        },
      },
    },
  },
};

const CHART_TYPE_PRESETS: Partial<Record<ChartType, HighchartsOptions>> = {
  horizontalStackedBar: HORIZONTAL_STACKED_BAR_PRESET,
};

export const getChartTypePreset = (
  chartType: ChartType
): HighchartsOptions | null => CHART_TYPE_PRESETS[chartType] ?? null;

export const applyChartTypePreset = (
  options: HighchartsOptions,
  preset: HighchartsOptions
): void => {
  if (preset.chart) {
    options.chart = { ...options.chart, ...preset.chart };
  }
  if (preset.legend) {
    options.legend = { ...options.legend, ...preset.legend };
  }
  if (preset.xAxis !== undefined) {
    options.xAxis = preset.xAxis;
  }
  if (preset.yAxis) {
    const existingYAxis =
      typeof options.yAxis === 'object' && !Array.isArray(options.yAxis)
        ? options.yAxis
        : {};
    options.yAxis = {
      ...existingYAxis,
      ...(typeof preset.yAxis === 'object' && !Array.isArray(preset.yAxis)
        ? preset.yAxis
        : {}),
    };
  }
  if (preset.plotOptions) {
    if (!options.plotOptions) options.plotOptions = {};
    if (preset.plotOptions.series) {
      options.plotOptions.series = {
        ...options.plotOptions.series,
        ...preset.plotOptions.series,
      };
    }
    if (preset.plotOptions.bar) {
      options.plotOptions.bar = {
        ...options.plotOptions.bar,
        ...preset.plotOptions.bar,
      };
    }
  }
};

/**
 * Check if a chart transition is valid
 * @param from - The current chart type
 * @param to - The target chart type
 * @returns boolean indicating if the transition is valid
 */
export const isValidChartTransition = (
  from: ChartType,
  to: ChartType
): boolean => {
  return SUPPORTED_CHART_TRANSITIONS[from]?.includes(to) ?? false;
};

/**
 * Process chart data to ensure it's compatible with the selected chart type
 * @param data - The input chart data
 * @param chartType - The target chart type
 * @returns Processed chart data
 */
export const processChartData = (
  data: ChartData,
  chartType: ChartType
): ChartSeriesData[] => {
  return data.map((series) => ({
    ...series,
    type: series.type || chartType,
  }));
};

interface ChartDataInput {
  type?: ChartType;
  series?: Array<{
    name?: string;
    y?: number;
    custom_id?: string;
    data?: unknown[];
  }>;
  categories?: string[];
  flowdt?: unknown;
  data?: unknown;
}

export const getSeriesData = (
  chartData: ChartDataInput | ChartDataInput[] | undefined,
  type: ChartType,
  customOptions: Record<string, unknown>
): ChartSeriesData[] => {
  const { legendName } = customOptions ?? {};
  if (!chartData) {
    return [];
  }

  const isInputArray = Array.isArray(chartData);
  const primary: ChartDataInput = isInputArray
    ? (chartData[0] ?? {})
    : chartData;
  const legendNameStr =
    typeof legendName === 'string' && legendName.length > 0
      ? legendName
      : undefined;

  if (type === 'pyramid') {
    return [
      {
        type: 'pyramid',
        ...(legendNameStr ? { name: legendNameStr } : {}),
        data:
          primary.series?.map((item) => ({
            name: item.name,
            y: item.y,
            custom_id: item.custom_id,
          })) ?? [],
      } as ChartSeriesData,
    ];
  }
  if (type === 'horizontalStackedBar') {
    return (
      primary.series?.map(
        (item) =>
          ({
            type: 'bar',
            name: item.name,
            data: [
              {
                y: item.y,
                custom_id: item.custom_id,
              },
            ],
          }) as ChartSeriesData
      ) ?? []
    );
  }
  if (type === 'area') {
    const { singleSeriesData: rawSingle = {} } = customOptions as Record<
      string,
      unknown
    >;
    const singleSeriesData = rawSingle as Record<string, unknown>;
    if (Array.isArray(primary?.series) && primary?.series?.[0]?.data) {
      const { name: _name, ...singleSeriesDataWithoutName } = singleSeriesData;
      return primary.series.map((series) => ({
        ...(series as Record<string, unknown>),
        ...singleSeriesDataWithoutName,
      })) as unknown as ChartSeriesData[];
    }
    return [
      {
        ...singleSeriesData,
        data: primary.series,
      } as ChartSeriesData,
    ];
  }
  if (type === 'column') {
    const { singleSeriesData: rawSingleCol = {} } = customOptions as Record<
      string,
      unknown
    >;
    const singleSeriesData = rawSingleCol as Record<string, unknown>;
    if (Array.isArray(primary?.series) && primary?.series?.[0]?.data) {
      const { name: _nameCol, ...singleSeriesDataWithoutName } =
        singleSeriesData;
      return primary.series.map((series) => ({
        ...(series as Record<string, unknown>),
        ...singleSeriesDataWithoutName,
      })) as unknown as ChartSeriesData[];
    }
    return [
      {
        ...singleSeriesData,
        data: primary.series,
      } as ChartSeriesData,
    ];
  }
  if (type === 'bar') {
    const { singleSeriesData: rawSingleBar = {} } = customOptions as Record<
      string,
      unknown
    >;
    const singleSeriesData = rawSingleBar as Record<string, unknown>;
    if (Array.isArray(primary?.series) && primary?.series?.[0]?.data) {
      const { name: _nameBar, ...singleSeriesDataWithoutName } =
        singleSeriesData;
      return primary.series.map((series) => ({
        ...(series as Record<string, unknown>),
        ...singleSeriesDataWithoutName,
      })) as unknown as ChartSeriesData[];
    }
    return [
      {
        ...singleSeriesData,
        data: primary.series,
      } as ChartSeriesData,
    ];
  }
  if (type === 'pie') {
    return [
      {
        data: primary?.series?.filter(({ y }) => y),
        ...(legendNameStr ? { name: legendNameStr } : {}),
      } as ChartSeriesData,
    ];
  }
  if (isInputArray) {
    return chartData.map(
      (series) =>
        ({
          ...series,
          type: series.type || type,
        }) as ChartSeriesData
    );
  }
  if (primary.series) {
    return primary.series.map(
      (series) =>
        ({
          ...series,
          type: (series as { type?: ChartType }).type || type,
        }) as ChartSeriesData
    );
  }
  return [
    {
      type,
      data: primary.data ?? primary,
    } as ChartSeriesData,
  ];
};
